import { useContext, useEffect } from "react";

import { MktgDataContext } from "@swa-ui/application";
import { usePrevious } from "@swa-ui/application";
import { useLocation } from "@swa-ui/browser";
import { swaDate } from "@swa-ui/date";

export function useSelectDatesAnalytics(lowFareCalendarData, searchParams) {
  const { pathname } = useLocation();
  const { setMktgPageData } = useContext(MktgDataContext);
  const prevSelectedDateDepartureDate = usePrevious(searchParams?.departureDate);
  const prevSelectedDateReturnDate = usePrevious(searchParams?.returnDate);

  useEffect(() => {
    if (pathname.includes("/air/low-fare-calendar/select-dates")) {
      const { adultPassengersCount, currencyCode, departureDate, promoCode, returnDate, tripType } =
        searchParams;
      const selectDatesAnalyticsObject = {};

      setBound2Defaults(selectDatesAnalyticsObject);
      const boundData = lowFareCalendarData?.searchResults?.reduce(
        (selectDatesAnalyticsObjectAccumulator, lowFareCalendarBoundData, id) => {
          const { destinationAirportCode, originationAirportCode } = lowFareCalendarBoundData;
          const boundId = id + 1;

          return {
            ...selectDatesAnalyticsObjectAccumulator,
            [`air_bound${boundId}_airportcode`]: `${originationAirportCode}:${destinationAirportCode}`,
            [`air_bound${boundId}_date`]: boundId === 1 ? departureDate : returnDate,
            [`air_bound${boundId}_month_selected`]: getMonth(
              boundId === 1 ? departureDate : returnDate
            ),
            [`lfc_bound${boundId}_lowestprice_currency`]:
              currencyCode === "USD" ? getLowestFareForBound(lowFareCalendarBoundData) : "NONE",
            [`lfc_bound${boundId}_lowestprice_points`]:
              currencyCode === "POINTS" ? getLowestFareForBound(lowFareCalendarBoundData) : "NONE",
            pricing_type: currencyCode.toLowerCase(),
          };
        },
        selectDatesAnalyticsObject
      );

      selectDatesAnalyticsObject[`number_passenger`] = adultPassengersCount;
      promoCode && (selectDatesAnalyticsObject[`promocode`] = promoCode);
      selectDatesAnalyticsObject[`tripType`] = getTripType(tripType);

      setMktgPageData(
        {
          ...selectDatesAnalyticsObject,
          ...boundData,
          product: "air",
          productView: "1",
        },
        "selectDates"
      );
    }
  }, [
    pathname,
    lowFareCalendarData,
    searchParams?.tripType,
    searchParams?.currencyCode,
    searchParams?.promoCode,
  ]);

  useEffect(() => {
    if (
      (prevSelectedDateDepartureDate &&
        prevSelectedDateDepartureDate !== searchParams?.departureDate) ||
      (prevSelectedDateReturnDate && prevSelectedDateReturnDate !== searchParams?.returnDate)
    ) {
      setMktgPageData(
        {
          lfc_month_changed: "1",
        },
        "selectDates"
      );
    }
  }, [searchParams?.departureDate, searchParams?.returnDate]);

  function setBound2Defaults(selectDatesAnalyticsObject) {
    selectDatesAnalyticsObject["air_bound2_airportcode"] = "NONE";
    selectDatesAnalyticsObject["air_bound2_date"] = "NONE";
    selectDatesAnalyticsObject["air_bound2_month_selected"] = "NONE";
    selectDatesAnalyticsObject["lfc_bound2_lowestprice_currency"] = "NONE";
    selectDatesAnalyticsObject["lfc_bound2_lowestprice_points"] = "NONE";

    return selectDatesAnalyticsObject;
  }

  function getLowestFareForBound(boundData) {
    const { lowFareCalendarDays } = boundData;
    let lowestFare = Infinity;

    lowFareCalendarDays?.forEach((day) => {
      const { fares } = day;

      Object.values(fares).forEach((fare) => {
        const totalFareValue = parseFloat(fare.totalFare?.value || Infinity);

        if (totalFareValue < lowestFare) {
          lowestFare = totalFareValue;
        }
      });
    });

    return lowestFare === Infinity ? "0" : `${lowestFare}`;
  }

  function getMonth(date) {
    return swaDate(date).format("MMM");
  }

  function getTripType(trip) {
    let tripType = "MC";

    if (trip === "roundtrip") {
      tripType = "RT";
    } else if (trip === "oneway") {
      tripType = "OW";
    }

    return tripType;
  }
}
